import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ASSETS, api } from "@/lib/api";
import { SafeImage } from "@/components/SafeImage";
import { setReferral, resolveReferral } from "@/lib/referral";
import Reservations from "@/components/site/Reservations";
import TicketDialog from "@/components/site/TicketDialog";
import ReservationDialog from "@/components/site/ReservationDialog";
import { Ticket, CalendarDays, ArrowUpRight, Loader2, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Book with X" landing page. This is what staff QR codes point to.
 * Bypasses the marketing homepage and drops the customer directly on
 * the two conversion actions: reserve a table + buy event tickets.
 */
export default function BookWithStaff() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [ticketEvent, setTicketEvent] = useState(null);
  const [rsvpEvent, setRsvpEvent] = useState(null);

  useEffect(() => {
    if (!code) return;
    // Stash the referral for downstream forms
    setReferral(code);
    (async () => {
      try {
        const s = await resolveReferral(API, code);
        if (!s) {
          navigate("/", { replace: true });
          return;
        }
        setStaff(s);
      } catch (_) {
        navigate("/", { replace: true });
        return;
      } finally {
        setLoading(false);
      }
    })();
  }, [code, navigate]);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data || [])).catch(() => setEvents([]));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--trio-bg)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--trio-primary)]" />
      </div>
    );
  }
  if (!staff) return null;

  const paidEvents = events.filter((e) => e.has_tickets && e.ticket_price);
  const rsvpEvents = events.filter((e) => !e.has_tickets && e.active);
  const firstName = (staff.name || "").split(" ")[0] || staff.name;

  return (
    <div className="min-h-screen bg-[var(--trio-bg)] text-[var(--trio-text)]">
      {/* Sticky header */}
      <header className="sticky top-0 z-30 bg-[var(--trio-text)] text-[var(--trio-bg)]">
        <div className="max-w-4xl mx-auto px-5 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2" data-testid="book-back-home">
            <img src={ASSETS.logo} alt="Trio" className="h-8 w-auto invert" />
          </Link>
          <div className="text-right">
            <div className="text-[9px] tracking-[0.3em] uppercase text-[var(--trio-accent)] font-bold">
              Booking With
            </div>
            <div className="font-display text-lg leading-tight" data-testid="book-staff-name">
              {staff.name}
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-5 pt-8 sm:pt-12 pb-6" data-testid="book-hero">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold">
          Eat. Drink. Vibe.
        </div>
        <h1 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-2">
          Book directly with {firstName}
        </h1>
        <p className="text-sm sm:text-base text-[var(--trio-muted)] mt-3 max-w-2xl leading-relaxed">
          Reserve your table or grab tickets for what's coming up — {firstName} will personally take care of you when you walk in.
        </p>
      </section>

      {/* Quick jump nav */}
      <nav className="sticky top-[52px] z-20 bg-[var(--trio-bg)] border-y border-[var(--trio-border)]" data-testid="book-jumpnav">
        <div className="max-w-4xl mx-auto px-5 flex items-center gap-1">
          <a href="#reserve" className="flex-1 text-center py-3 text-xs tracking-widest uppercase font-bold border-r border-[var(--trio-border)] hover:bg-[var(--trio-text)] hover:text-[var(--trio-bg)] transition-colors">
            Reserve a Table
          </a>
          <a href="#events" className="flex-1 text-center py-3 text-xs tracking-widest uppercase font-bold hover:bg-[var(--trio-text)] hover:text-[var(--trio-bg)] transition-colors">
            Buy Event Tickets
            {paidEvents.length > 0 && (
              <span className="ml-2 inline-block bg-[var(--trio-primary)] text-white px-1.5 py-0.5 text-[10px]">
                {paidEvents.length}
              </span>
            )}
          </a>
        </div>
      </nav>

      {/* Paid events — shown first if any exist so they're impossible to miss */}
      {paidEvents.length > 0 && (
        <section id="events" className="max-w-4xl mx-auto px-5 py-8 sm:py-12 space-y-4" data-testid="book-events">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="font-display uppercase text-3xl tracking-tight flex items-center gap-2">
              <Ticket className="w-6 h-6 text-[var(--trio-primary)]" />
              Buy Tickets
            </h2>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-muted)] font-bold">
              {paidEvents.length} event{paidEvents.length !== 1 ? "s" : ""}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {paidEvents.map((ev) => (
              <PaidEventCard key={ev.id} ev={ev} onBuy={() => setTicketEvent(ev)} />
            ))}
          </div>
        </section>
      )}

      {/* Reservation form */}
      <section id="reserve" className="bg-[var(--trio-text)] text-[var(--trio-bg)] py-12" data-testid="book-reserve">
        <div className="max-w-4xl mx-auto px-5">
          <div className="mb-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-accent)] font-bold">
              Reserve a Table
            </div>
            <h2 className="font-display uppercase text-3xl tracking-tight mt-1">
              Let's get you a spot
            </h2>
          </div>
          <div className="bg-[var(--trio-bg)] text-[var(--trio-text)] p-5 sm:p-8">
            <Reservations />
          </div>
        </div>
      </section>

      {/* RSVP-only events (secondary) */}
      {rsvpEvents.length > 0 && (
        <section className="max-w-4xl mx-auto px-5 py-10 space-y-4" data-testid="book-rsvp-events">
          <h2 className="font-display uppercase text-2xl tracking-tight flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-[var(--trio-primary)]" />
            RSVP for the Vibe
          </h2>
          <div className="grid gap-2">
            {rsvpEvents.map((ev) => (
              <button
                key={ev.id}
                onClick={() => setRsvpEvent(ev)}
                className="text-left border border-[var(--trio-border)] bg-[var(--trio-surface)] p-4 hover:border-[var(--trio-primary)] transition-colors flex items-center justify-between gap-3"
                data-testid={`book-rsvp-${ev.id}`}
              >
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold">
                    {ev.event_date} {ev.event_time ? `· ${ev.event_time}` : ""}
                  </div>
                  <div className="font-display text-xl tracking-tight mt-0.5">{ev.title}</div>
                  {ev.description && (
                    <div className="text-xs text-[var(--trio-muted)] line-clamp-1 mt-0.5">{ev.description}</div>
                  )}
                </div>
                <ArrowUpRight className="w-4 h-4 flex-shrink-0" />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Footer with back link */}
      <footer className="border-t border-[var(--trio-border)] py-10 text-center">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-[var(--trio-muted)] hover:text-[var(--trio-primary)]"
        >
          <ArrowLeft className="w-4 h-4" /> Full site
        </Link>
      </footer>

      <TicketDialog
        open={!!ticketEvent}
        onOpenChange={(o) => !o && setTicketEvent(null)}
        event={ticketEvent}
      />
      <ReservationDialog
        open={!!rsvpEvent}
        onOpenChange={(o) => !o && setRsvpEvent(null)}
        mode="event"
        event={rsvpEvent}
      />
    </div>
  );
}

function PaidEventCard({ ev, onBuy }) {
  const capacityLeft =
    ev.ticket_capacity != null
      ? Math.max(0, ev.ticket_capacity - (ev.tickets_sold || 0))
      : null;
  const soldOut = capacityLeft === 0;
  return (
    <div
      className="border border-[var(--trio-border)] bg-[var(--trio-surface)] flex flex-col overflow-hidden"
      data-testid={`book-event-${ev.id}`}
    >
      {ev.image_url && (
        <div className="aspect-square overflow-hidden">
          <SafeImage src={ev.image_url} alt={ev.title} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-4 flex-1 flex flex-col">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold">
          {ev.event_date} {ev.event_time ? `· ${ev.event_time}` : ""}
        </div>
        <div className="font-display text-xl tracking-tight mt-1">{ev.title}</div>
        {ev.description && (
          <p className="text-xs text-[var(--trio-muted)] mt-1 line-clamp-2 flex-1">
            {ev.description}
          </p>
        )}
        <div className="mt-4 flex items-end justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--trio-muted)]">
              From
            </div>
            <div className="font-display text-3xl text-[var(--trio-primary)] leading-none">
              ${Number(ev.ticket_price).toFixed(0)}
            </div>
            {capacityLeft != null && !soldOut && (
              <div className="text-[10px] text-[var(--trio-muted)] uppercase tracking-widest mt-0.5">
                {capacityLeft} left
              </div>
            )}
          </div>
          <button
            onClick={onBuy}
            disabled={soldOut}
            data-testid={`book-buy-${ev.id}`}
            className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] disabled:opacity-40 disabled:cursor-not-allowed text-white px-5 py-2.5 text-xs tracking-widest uppercase font-bold"
          >
            {soldOut ? "Sold Out" : "Buy Tickets"}
            {!soldOut && <ArrowUpRight className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
